import axios from "axios";
import Swal from "sweetalert2";
import { isAdminHost, isPublicHost } from "./hosts";

const SESSION_KEYS = [
  "token",
  "access_token",
  "authToken",
  "user",
  "userInfo",
  "role",
  "session",
  "profile",
  "Umwe_Kumubumbe",
  "lastActivityAt",
];

/** End the session after this much idle time (activity resets the clock). */
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_STORAGE_KEY = "lastActivityAt";
const ACTIVITY_THROTTLE_MS = 1000;
const IDLE_CHECK_MS = 10000;
/** Intentional activity only — avoid mousemove/scroll (they make the UI feel laggy). */
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "click"];

let redirecting = false;
let lastActivityWrite = 0;

export function tokenExpiryMs(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return json?.exp ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const expiry = tokenExpiryMs(token);
  return expiry != null && expiry <= Date.now();
}

export function cvReturnPath(value) {
  const next = String(value || "");
  if (!next.startsWith("/cv?") || next.startsWith("//") || next.includes("\\")) return "";
  try {
    const url = new URL(next, "http://recruitment.mtn.co.rw");
    if (url.origin !== "http://recruitment.mtn.co.rw" || url.pathname !== "/cv") return "";
    const file = url.searchParams.get("file") || "";
    if (!file || file.includes("/") || file.includes("\\") || file.includes("..")) return "";
    return `/cv?file=${encodeURIComponent(file)}`;
  } catch {
    return "";
  }
}

export function isPublicPath(path = window.location.pathname) {
  if (path === "/cv" || path.startsWith("/cv/")) return true;
  if (isAdminHost()) return path === "/login_01";
  if (path === "/" || path === "/career") return true;
  if (path.startsWith("/apply/")) return true;
  return /^\/jobs\/[^/]+$/.test(path);
}

export function clearBrowserSession() {
  SESSION_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
  try {
    localStorage.clear();
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
}

export function markSessionActivity() {
  const now = Date.now();
  if (now - lastActivityWrite < ACTIVITY_THROTTLE_MS) return;
  lastActivityWrite = now;
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
  } catch {
    /* ignore */
  }
}

export function getLastActivityAt() {
  try {
    const raw = Number(localStorage.getItem(ACTIVITY_STORAGE_KEY) || 0);
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  } catch {
    return 0;
  }
}

export function expireSession({ reason = "expired" } = {}) {
  if (redirecting || isPublicPath()) return;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token && !localStorage.getItem("user") && !localStorage.getItem("access_token")) return;
  redirecting = true;
  clearBrowserSession();
  if (isPublicHost()) {
    window.location.replace("/");
    return;
  }
  const back = cvReturnPath(`${window.location.pathname}${window.location.search}`);
  const next = back ? `&next=${encodeURIComponent(back)}` : "";
  const why = reason === "idle" ? "idle" : "1";
  window.location.replace(`/login_01?expired=${why}${next}`);
}

export function showSessionExpired() {
  const params = new URLSearchParams(window.location.search);
  const expired = params.get("expired");
  if (!expired) return;
  const back = cvReturnPath(params.get("next"));
  window.history.replaceState({}, "", back ? `/login_01?next=${encodeURIComponent(back)}` : "/login_01");
  const idle = expired === "idle";
  Swal.fire({
    title: idle ? "Signed out due to inactivity" : "Session expired",
    text: idle
      ? "You were idle for 30 minutes, so your session ended. Please sign in again."
      : "Please sign in again.",
    icon: "warning",
    confirmButtonText: "OK",
    confirmButtonColor: "#FFCB05",
    allowOutsideClick: false,
  });
}

function requestHadAuth(headers) {
  if (!headers) return false;
  if (typeof headers.get === "function") {
    return Boolean(headers.get("Authorization") || headers.get("authorization"));
  }
  return Boolean(headers.Authorization || headers.authorization);
}

function hasAuthToken() {
  return Boolean(
    localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("token")
  );
}

function installIdleTimeout() {
  const onActivity = () => {
    if (!hasAuthToken() || isPublicPath()) return;
    markSessionActivity();
  };

  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onActivity, { passive: true, capture: true });
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") onActivity();
  });

  window.addEventListener("storage", (event) => {
    if (event.key === ACTIVITY_STORAGE_KEY && event.newValue) {
      lastActivityWrite = Date.now();
    }
  });

  if (hasAuthToken() && !getLastActivityAt()) {
    markSessionActivity();
  }

  window.setInterval(() => {
    if (!hasAuthToken() || isPublicPath()) return;
    const last = getLastActivityAt() || Date.now();
    if (Date.now() - last >= IDLE_TIMEOUT_MS) {
      expireSession({ reason: "idle" });
    }
  }, IDLE_CHECK_MS);
}

export function installSessionGuards() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = String(error?.config?.url || "");
      if (status === 401 && requestHadAuth(error?.config?.headers) && !/\/(login|auth)\b/i.test(url)) {
        expireSession({ reason: "expired" });
      }
      return Promise.reject(error);
    }
  );

  axios.interceptors.request.use((config) => {
    if (hasAuthToken() && !isPublicPath()) markSessionActivity();
    return config;
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    if (hasAuthToken() && !isPublicPath()) markSessionActivity();
    const response = await originalFetch(input, init);
    const url = typeof input === "string" ? input : input?.url || "";
    if (response.status === 401 && requestHadAuth(init.headers) && !/\/(login|auth)\b/i.test(url)) {
      expireSession({ reason: "expired" });
    }
    return response;
  };

  const checkJwt = () => {
    const token = localStorage.getItem("token") || localStorage.getItem("access_token");
    if (token && isTokenExpired(token)) expireSession({ reason: "expired" });
  };
  checkJwt();
  window.setInterval(checkJwt, 15000);
  window.addEventListener("focus", checkJwt);

  installIdleTimeout();
}
